<template>
    <generic-page>
        <b-btn
            variant="link"
            size="lg"
            class="pl-0"
            :to="`/creator/creatorVideo/edit/${creatorVideoId}`"
        >
            <font-awesome-icon size="lg" icon="fa-light fa-chevron-left" />
            Back to Video
        </b-btn>

        <div class="d-flex align-items-center justify-content-between">
            <h5 class="mb-0">Invite Customers</h5>
            <wallet-balance />
        </div>
        <div class="text-secondary small mt-1 mb-3">
            Personally invite your followers to watch your video.
            Each invite costs 1 credit.
        </div>

        <div class="invite-perks">
            <div class="invite-perk">
                <font-awesome-icon
                    icon="fa-duotone fa-envelope"
                    class="text-primary"
                />
                <div class="small">
                    <strong>Personal Email</strong>
                    <div class="text-secondary">
                        Each customer receives a direct email with a link to
                        your video
                    </div>
                </div>
            </div>
            <div class="invite-perk">
                <font-awesome-icon
                    icon="fa-duotone fa-bell"
                    class="text-info"
                />
                <div class="small">
                    <strong>Push Notification</strong>
                    <div class="text-secondary">
                        A push notification is delivered to their device
                        when available
                    </div>
                </div>
            </div>
        </div>

        <b-alert v-if="error" variant="danger" show>
            {{ error }}
        </b-alert>

        <div class="py-2">
            <div v-if="loading">
                <generic-loading />
            </div>
            <div v-else-if="interests && interests.length">
                <div
                    :key="interest.defaultMap.id"
                    v-for="interest in interests"
                >
                    <user-standard-avatar
                        :standardMap="interest.standardMap"
                        :timestamp="interest.defaultMap.created"
                        :nonPrimaryPhotos="false"
                        :hideBadges="true"
                        :userBetweenStatus="false"
                        size="small"
                        class="border-light border-top"
                    >
                        <div v-if="hasInvitedUserId(interest.standardMap.id)">
                            <font-awesome-icon
                                class="text-success"
                                icon="fa-light fa-check"
                            />
                            Invited
                        </div>
                        <div v-else>
                            <b-btn
                                variant="primary"
                                pill
                                size="sm"
                                :disabled="sendingUserId === interest.standardMap.id"
                                @click="sendInvite(interest.standardMap.id)"
                            >
                                <span v-if="sendingUserId === interest.standardMap.id">
                                    <font-awesome-icon
                                        icon="fa-light fa-spinner-third"
                                        spin
                                    />
                                </span>
                                <span v-else>
                                    Invite
                                    <coin-badge :amount="1" />
                                </span>
                            </b-btn>
                        </div>
                    </user-standard-avatar>
                </div>
            </div>
            <div v-else class="text-center text-secondary py-4">
                No followers yet
            </div>
        </div>

        <generic-pagination
            @next="next"
            @previous="previous"
            :hasMorePages="hasMorePages"
            :page="page"
        />
    </generic-page>
</template>

<style scoped>
.invite-perks {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 12px;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 10px;
    margin-bottom: 12px;
}

.invite-perk {
    display: flex;
    align-items: flex-start;
    gap: 10px;
}
</style>

<script>
import UserStandardAvatar from "@/components/user/UserStandardAvatar";
import WalletBalance from "@/components/utility/WalletBalance";
import GenericPagination from "@/components/utility/GenericPagination";

export default {
    data() {
        return {
            page: 1,
            hasMorePages: false,
            interests: null,
            inviteUserIds: null,
            loading: false,
            sendingUserId: null,
            error: null,
        };
    },
    computed: {
        creatorVideoId() {
            return this.$route.params?.id;
        },
    },
    created() {
        this.refreshInvitedUserIds();
        this.refreshFollowers();
    },
    methods: {
        hasInvitedUserId(userId) {
            return this.inviteUserIds?.includes(userId);
        },
        refreshFollowers() {
            this.loading = true;

            this.api
                .post("/interest/index", {
                    page: this.page,
                    direction: "inbound",
                })
                .then((response) => {
                    this.interests = response?.data?.interests;
                    this.hasMorePages = response?.data?.hasMorePages;
                })
                .catch(() => {})
                .finally(() => {
                    this.loading = false;
                });
        },
        refreshInvitedUserIds() {
            this.api
                .post("/creatorVideoInvite/getInvitedUserIds", {
                    creatorVideoId: this.creatorVideoId,
                })
                .then((response) => {
                    if (response?.data?.inviteUserIds) {
                        this.inviteUserIds = response.data.inviteUserIds;
                    }
                });
        },
        sendInvite(userId) {
            this.sendingUserId = userId;
            this.error = null;

            this.api
                .post("/creatorVideoInvite/create", {
                    creatorVideoId: this.creatorVideoId,
                    userId,
                })
                .then(() => {
                    this.refreshInvitedUserIds();
                    this.$store.dispatch("onboarding/status");
                })
                .catch((err) => {
                    const errors =
                        err?.response?.data?.errors?.creatorVideo;
                    if (errors?.length) {
                        this.error = errors.join(", ");
                    }
                })
                .finally(() => {
                    this.sendingUserId = null;
                });
        },
        next() {
            this.page++;
            this.refreshFollowers();
        },
        previous() {
            this.page--;
            this.refreshFollowers();
        },
    },
    components: {
        UserStandardAvatar,
        WalletBalance,
        GenericPagination,
    },
};
</script>
